import { bytesToBase64, utf8ToBytes } from '../crypto/bytes.ts';

export const SAFETY_ENVELOPE_VERSION = 1;
export const SAFETY_ENVELOPE_ALG = 'A256GCM';
export const SAFETY_ENVELOPE_PURPOSE = 'school-safety';

const ECDH_P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;

function schoolSafetyHkdfParams() {
  return {
    name: 'HKDF' as const,
    hash: 'SHA-256',
    salt: utf8ToBytes('farfar-school-safety-hkdf-salt-v1'),
    info: utf8ToBytes('farfar-school-safety-wrap-v1')
  };
}

export type SchoolSafetyEnvelope = {
  v: 1;
  alg: 'A256GCM';
  purpose: 'school-safety';
  schoolId: string;
  ephemeralPublicKeyJwk: JsonWebKey;
  iv: string;
  ciphertext: string;
};

async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  const { d, key_ops, ...pub } = jwk;
  return crypto.subtle.importKey('jwk', { ...pub, key_ops: [] }, ECDH_P256, true, []);
}

async function deriveSchoolSafetyWrapKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  const hkdfBase = await crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    schoolSafetyHkdfParams(),
    hkdfBase,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function wrapPlaintextForSchoolSafety(
  plaintext: string,
  schoolId: string,
  recipientPublicJwk: JsonWebKey
): Promise<SchoolSafetyEnvelope> {
  const ephemeral = await crypto.subtle.generateKey(ECDH_P256, true, ['deriveBits']);
  const recipientPub = await importPublicKey(recipientPublicJwk);
  const wrapKey = await deriveSchoolSafetyWrapKey(ephemeral.privateKey, recipientPub);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrapKey,
    utf8ToBytes(plaintext)
  );
  const ephemeralPublicKeyJwk = await crypto.subtle.exportKey('jwk', ephemeral.publicKey);
  delete ephemeralPublicKeyJwk.d;
  return {
    v: SAFETY_ENVELOPE_VERSION,
    alg: SAFETY_ENVELOPE_ALG,
    purpose: SAFETY_ENVELOPE_PURPOSE,
    schoolId,
    ephemeralPublicKeyJwk,
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext))
  };
}
